/**
 * Client hooks for the Info hub. All data comes from the same-origin /api/info/* and
 * /api/multiswap/stats routes (server does the source work: subgraph, RPC, pricing, caching).
 * SWR settings: bounded automatic retry (3 × 4 s), keep previous data on refresh, manual retry via mutate.
 */
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'
import useSWR, { SWRConfiguration } from 'swr'
import type { DexId, DexVersion, InfoSummary, PoolDetail, TokenDetail } from 'server/info/types'
import type { MultiSwapStats } from 'server/multiswapRecord/types'
import type { SmartSwapRange, SmartSwapReport } from 'server/multiswapRecord/smartSwap'

export type { DexId, DexVersion }
export type VersionParam = DexVersion | 'all'

export const DEX_LABEL: Record<DexId, string> = { cadinu: 'CADINUdex', pancakeswap: 'PancakeSwap' }
export const DEX_IDS: DexId[] = ['cadinu', 'pancakeswap']
/**
 * The DEX tabs of Markets. 2026-10-06 (Bez): PancakeSwap is no longer a tab ("Smart Swap" takes its place, /info/smart-swap); its pool
 * and token pages stay reachable from the CADINU and CBON token pages, so 'pancakeswap' is still a known DEX in the URL.
 */
export const TAB_DEX_IDS: DexId[] = ['cadinu']
export const SMART_SWAP_PATH = '/info/smart-swap'
export const isDexId = (v: unknown): v is DexId => v === 'cadinu' || v === 'pancakeswap'

const SETTINGS: SWRConfiguration = {
  refreshInterval: 30_000,
  keepPreviousData: true,
  errorRetryCount: 3,
  errorRetryInterval: 4_000,
  revalidateOnFocus: false,
  dedupingInterval: 5_000,
}

class HttpError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

const fetcher = async <T>(url: string): Promise<T> => {
  const res = await fetch(url, { headers: { accept: 'application/json' } })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      if (j?.error) msg = String(j.error)
    } catch {
      /* ignore */
    }
    throw new HttpError(res.status, msg)
  }
  return res.json()
}

export const parseVersion = (v: unknown): VersionParam => (v === 'v2' || v === 'v3' ? v : 'all')

/** dex / version / section from the URL: /info/[dex]/(pools|tokens)/[address]?v=v2|v3 */
export const useInfoRoute = () => {
  const router = useRouter()
  const dex: DexId | null = isDexId(router.query.dex) ? router.query.dex : null
  const version = parseVersion(router.query.v)
  const address = typeof router.query.address === 'string' ? router.query.address.toLowerCase() : null
  const section: 'overview' | 'pools' | 'tokens' = router.pathname.includes('/pools') ? 'pools' : router.pathname.includes('/tokens') ? 'tokens' : 'overview'
  const hrefFor = useCallback(
    (nextDex: DexId | 'total', nextVersion: VersionParam, nextSection: 'overview' | 'pools' | 'tokens' = section) => {
      if (nextDex === 'total') return '/info'
      const base = `/info/${nextDex}${nextSection === 'overview' ? '' : `/${nextSection}`}`
      return nextVersion === 'all' ? base : `${base}?v=${nextVersion}`
    },
    [section],
  )
  return { dex, version, address, section, hrefFor, isReady: router.isReady, isSmartSwap: router.pathname === SMART_SWAP_PATH }
}

export const useInfoSummary = (dex: DexId | null, version: VersionParam) => {
  const key = dex ? `/api/info/summary?dex=${dex}&version=${version}` : null
  const { data, error, isLoading, isValidating, mutate } = useSWR<InfoSummary>(key, fetcher, SETTINGS)
  const retry = useCallback(() => mutate(), [mutate])
  return { summary: data, error: error as Error | undefined, isLoading, isValidating, retry }
}

export const usePoolDetail = (dex: DexId | null, version: VersionParam, address: string | null) => {
  const v = version === 'all' ? null : version
  const key = dex && v && address ? `/api/info/pool?dex=${dex}&version=${v}&address=${address}` : null
  const { data, error, isLoading, mutate } = useSWR<PoolDetail>(key, fetcher, SETTINGS)
  return { detail: data, error: error as Error | undefined, isLoading, retry: () => mutate(), needsVersion: !v }
}

export const useTokenDetail = (dex: DexId | null, version: VersionParam, address: string | null) => {
  const key = dex && address ? `/api/info/token?dex=${dex}&version=${version}&address=${address}` : null
  const { data, error, isLoading, mutate } = useSWR<TokenDetail>(key, fetcher, SETTINGS)
  return { detail: data, error: error as Error | undefined, isLoading, retry: () => mutate() }
}

export const useMultiSwapStats = (chainId = 56) => {
  const { data, error, isLoading, mutate } = useSWR<MultiSwapStats>(`/api/multiswap/stats?chainId=${chainId}`, fetcher, SETTINGS)
  return useMemo(() => ({ stats: data, error: error as Error | undefined, isLoading, retry: () => mutate() }), [data, error, isLoading, mutate])
}

/** Markets "Smart Swap" tab: /api/multiswap/smart-swap for one range (24h, 7d, all). */
export const useSmartSwap = (range: SmartSwapRange, chainId = 56) => {
  const { data, error, isLoading, mutate } = useSWR<SmartSwapReport>(`/api/multiswap/smart-swap?chainId=${chainId}&range=${range}`, fetcher, SETTINGS)
  return useMemo(() => ({ report: data, error: error as Error | undefined, isLoading, retry: () => mutate() }), [data, error, isLoading, mutate])
}
